class InstagramGallery extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.updateArrows = this.updateArrows.bind(this);
    this.lastTrigger = null;
    this.slideIndex = 0;
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.content = this.querySelector('[data-instagram-gallery-content]');
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('close', this.onDialogClose);
    this.track = this.querySelector('[data-instagram-gallery-track]');
    this.track.addEventListener('scroll', this.updateArrows, { passive: true });
    this.resizeObserver = new ResizeObserver(this.updateArrows);
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.dialog.removeEventListener('click', this.onDialogClick);
    this.dialog.removeEventListener('close', this.onDialogClose);
    this.track.removeEventListener('scroll', this.updateArrows);
    this.resizeObserver.disconnect();
    document.body.classList.remove('instagram-gallery-open');
  }

  onClick(event) {
    const filter = event.target.closest('[data-gallery-filter]');
    if (filter) return this.filterPosts(filter);
    const trigger = event.target.closest('[data-instagram-gallery-open]');
    if (trigger) return this.open(trigger);
    if (event.target.closest('[data-instagram-gallery-close]')) return this.dialog.close();
    if (event.target.closest('[data-instagram-gallery-scroll-prev]')) return this.scrollTrack(-1);
    if (event.target.closest('[data-instagram-gallery-scroll-next]')) return this.scrollTrack(1);
    if (event.target.closest('[data-instagram-gallery-prev-post]')) return this.goPost(-1);
    if (event.target.closest('[data-instagram-gallery-next-post]')) return this.goPost(1);
    if (event.target.closest('[data-instagram-gallery-prev]')) return this.goSlide(-1);
    if (event.target.closest('[data-instagram-gallery-next]')) return this.goSlide(1);
  }

  onKeydown(event) {
    if (event.key === 'Tab' && this.dialog.open) {
      const controls = [...this.dialog.querySelectorAll('a[href], button:not(:disabled), [tabindex="0"]')].filter(element => element.getClientRects().length && !element.closest('[hidden]'));
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
        return;
      }
    }
    if (this.dialog.open && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      const step = event.key === 'ArrowLeft' ? -1 : 1;
      if (event.target.closest('.instagram-gallery__media')) return this.goSlide(step);
      return this.goPost(step);
    }
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const control = event.target.closest(
      '[data-instagram-gallery-open], [data-instagram-gallery-close], [data-instagram-gallery-prev], [data-instagram-gallery-next], [data-instagram-gallery-scroll-prev], [data-instagram-gallery-scroll-next], [data-instagram-gallery-prev-post], [data-instagram-gallery-next-post]'
    );
    if (!control || control.tagName === 'BUTTON') return;
    event.preventDefault();
    control.click();
  }

  // Clicks on the backdrop target the dialog element itself.
  onDialogClick(event) {
    if (event.target === this.dialog) this.dialog.close();
  }

  onDialogClose() {
    document.body.classList.remove('instagram-gallery-open');
    this.content.replaceChildren();
    if (this.lastTrigger) this.lastTrigger.focus();
  }

  open(trigger) {
    this.lastTrigger = trigger;
    this.render(trigger);
    document.body.classList.add('instagram-gallery-open');
    this.dialog.showModal();
  }

  render(trigger) {
    const template = this.querySelector(
      `template[data-instagram-gallery-post="${trigger.dataset.instagramGalleryOpen}"]`
    );
    if (!template) return;
    const restoreContentFocus = this.content.contains(document.activeElement);
    this.content.replaceChildren(template.content.cloneNode(true));
    this.slides = this.content.querySelector('.instagram-gallery__slides');
    this.slideIndex = 0;
    const count = this.content.querySelectorAll('[data-instagram-gallery-slide]').length;
    this.content.querySelector('[data-instagram-gallery-arrows]').hidden = count < 2;
    const postCount = this.querySelectorAll('.instagram-gallery__item:not([hidden]) .instagram-gallery__tile').length;
    this.querySelectorAll('[data-instagram-gallery-prev-post], [data-instagram-gallery-next-post]').forEach(button => { button.disabled = postCount < 2; });
    this.content.scrollTop = 0;
    if (restoreContentFocus) this.slides.focus({ preventScroll: true });
  }

  // Scroll the tile track by one visible page.
  scrollTrack(step) {
    this.track.scrollBy({ left: step * this.track.clientWidth });
  }

  // Show each arrow only when there is more to scroll in that direction.
  updateArrows() {
    if (this.classList.contains('instagram-gallery--page')) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    this.querySelector('[data-instagram-gallery-scroll-prev]').hidden = this.track.scrollLeft < 1;
    this.querySelector('[data-instagram-gallery-scroll-next]').hidden = this.track.scrollLeft > max - 1;
  }

  // Switch to the previous/next post, wrapping around.
  goPost(step) {
    const triggers = [...this.querySelectorAll('.instagram-gallery__tile[data-instagram-gallery-open]')].filter(trigger => !trigger.closest('li').hidden);
    const current = triggers.findIndex(trigger => trigger.dataset.instagramGalleryOpen === this.lastTrigger.dataset.instagramGalleryOpen);
    if (triggers.length < 2 || current === -1) return;
    this.lastTrigger = triggers[(current + step + triggers.length) % triggers.length];
    this.render(this.lastTrigger);
  }

  filterPosts(filter) {
    const category = filter.dataset.galleryFilter;
    this.querySelectorAll('[data-gallery-filter]').forEach(button => button.setAttribute('aria-pressed', String(button === filter)));
    let count = 0;
    this.querySelectorAll('[data-gallery-category]').forEach(item => {
      item.hidden = category !== 'all' && item.dataset.galleryCategory !== category;
      if (!item.hidden) count += 1;
    });
    const label = this.querySelector('[data-gallery-count]');
    label.textContent = count === 1 ? this.dataset.countLabelOne : this.dataset.countLabel.replace('[count]', count);
  }

  // Switch between the images of the current post.
  goSlide(step) {
    const count = this.content.querySelectorAll('[data-instagram-gallery-slide]').length;
    if (count < 2) return;
    this.slideIndex = (Math.round(this.slides.scrollLeft / this.slides.clientWidth) + step + count) % count;
    this.slides.scrollTo({ left: this.slides.clientWidth * this.slideIndex });
  }
}

if (!customElements.get('instagram-gallery')) {
  customElements.define('instagram-gallery', InstagramGallery);
}
